import { useState } from 'react';
import {
  buildInitialTerms,
  decideSave,
  type MappingMode,
  type MappingValidationError,
  normalizeTerms,
  removeTerm,
  replaceTerm,
  updatePayload,
} from './mappingForm';

interface CreateInput {
  partyId: string;
  productVariantId: string;
  terms: string[];
}

interface UpdateInput {
  terms: string[];
  productVariantId?: string;
}

export function useMappingFormState(args: {
  mode: MappingMode;
  initialPartyId?: string;
  initialProductId?: string;
  initialVariantId?: string;
  initialTerms?: string[] | null;
  savedVariantId?: string;
  onInvalid: (error: MappingValidationError) => void;
  onCreate: (input: CreateInput) => void;
  onUpdate: (input: UpdateInput) => void;
}) {
  const [partyId, setPartyId] = useState(args.initialPartyId);
  const [productId, setProductId] = useState(args.initialProductId);
  const [productVariantId, setProductVariantId] = useState(
    args.initialVariantId,
  );
  const [terms, setTerms] = useState(() =>
    buildInitialTerms(args.initialTerms),
  );
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [moveConfirmOpen, setMoveConfirmOpen] = useState(false);
  const [changingProduct, setChangingProduct] = useState(false);
  const normalizedTerms = normalizeTerms(terms);

  const commit = () => {
    if (args.mode === 'edit') {
      args.onUpdate(
        updatePayload({
          terms,
          savedVariantId: args.savedVariantId,
          selectedVariantId: productVariantId,
        }),
      );
      return;
    }
    if (!partyId || !productVariantId) return;
    args.onCreate({ partyId, productVariantId, terms: normalizedTerms });
  };

  const save = () => {
    const decision = decideSave({
      isEdit: args.mode === 'edit',
      partyId,
      productVariantId,
      terms,
      savedVariantId: args.savedVariantId,
    });
    if (decision.kind === 'invalid') {
      args.onInvalid(decision.error);
      return;
    }
    if (decision.kind === 'confirm-move') {
      setMoveConfirmOpen(true);
      return;
    }
    commit();
  };

  return {
    partyId,
    setPartyId,
    productId,
    productVariantId,
    setProduct: (nextProductId: string, nextVariantId: string) => {
      setProductId(nextProductId);
      setProductVariantId(nextVariantId);
    },
    terms,
    normalizedTerms,
    deleteConfirmOpen,
    setDeleteConfirmOpen,
    moveConfirmOpen,
    setMoveConfirmOpen,
    changingProduct,
    setChangingProduct,
    changeTerm: (index: number, value: string) => {
      setTerms((prev) => replaceTerm(prev, index, value));
    },
    addTerm: () => setTerms((prev) => [...prev, '']),
    removeTermAt: (index: number) => {
      setTerms((prev) => removeTerm(prev, index));
    },
    cancelProductChange: () => {
      setProductId(args.initialProductId);
      setProductVariantId(args.initialVariantId);
      setChangingProduct(false);
    },
    save,
    confirmMove: () => {
      setMoveConfirmOpen(false);
      commit();
    },
  };
}
